import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api, { formatPrice } from '../utils/api';
import toast from 'react-hot-toast';
import './Checkout.css';

// ── Stripe elements (lazy-loaded) ──────────────────────────
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY || 'pk_test_placeholder');

// ─────────────────────────────────────────────────────────────
// Step 1: Shipping form
// ─────────────────────────────────────────────────────────────
function ShippingStep({ onNext, initialData }) {
  const { user } = useAuth();
  const [form, setForm] = useState(initialData || {
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'India',
  });

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext(form);
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <h2 className="step-title">📦 Shipping Address</h2>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Full Name *</label>
          <input name="name" value={form.name} onChange={handleChange} className="form-input" required />
        </div>
        <div className="form-group">
          <label className="form-label">Email *</label>
          <input name="email" type="email" value={form.email} onChange={handleChange} className="form-input" required />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Phone *</label>
        <input name="phone" value={form.phone} onChange={handleChange} className="form-input" required placeholder="+91 XXXXX XXXXX" />
      </div>
      <div className="form-group">
        <label className="form-label">Street Address *</label>
        <input name="street" value={form.street} onChange={handleChange} className="form-input" required placeholder="House no., Street, Area" />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">City *</label>
          <input name="city" value={form.city} onChange={handleChange} className="form-input" required />
        </div>
        <div className="form-group">
          <label className="form-label">State *</label>
          <input name="state" value={form.state} onChange={handleChange} className="form-input" required />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">PIN Code *</label>
          <input name="postalCode" value={form.postalCode} onChange={handleChange} className="form-input" required pattern="[0-9]{6}" placeholder="6-digit PIN" />
        </div>
        <div className="form-group">
          <label className="form-label">Country</label>
          <input name="country" value={form.country} onChange={handleChange} className="form-input" />
        </div>
      </div>
      <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }}>
        Continue to Payment →
      </button>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 2: Payment method selector + pay
// ─────────────────────────────────────────────────────────────
function PaymentStep({ orderId, onSuccess, onBack }) {
  const [method, setMethod] = useState('razorpay');
  const [processing, setProcessing] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  // ── Razorpay ──────────────────────────────────────
  const payWithRazorpay = async () => {
  // Check if Razorpay script is loaded
  if (!window.Razorpay) {
    toast.error('Razorpay failed to load. Please refresh the page.');
    return;
  }
  setProcessing(true);
  try {
    const { data } = await api.post('/payments/razorpay/create-order', { orderId });

    const options = {
      key: data.keyId,
      amount: data.amount,
      currency: data.currency,
      name: 'PlantShop 🌿',
      description: 'Plants & Gardening Equipment',
      order_id: data.razorpayOrderId,
      handler: async (response) => {
        try {
          await api.post('/payments/razorpay/verify', {
            orderId,
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          });
          toast.success('Payment successful!');
          onSuccess(orderId);
        } catch (err) {
          toast.error(err.response?.data?.message || 'Payment verification failed');
          setProcessing(false);
        }
      },
      prefill: {},
      theme: { color: '#2e8b4a' },
      modal: {
        ondismiss: () => {
          setProcessing(false);
          toast('Payment cancelled');
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (response) => {
      toast.error(`Payment failed: ${response.error.description}`);
      setProcessing(false);
    });
    rzp.open();

  } catch (err) {
    toast.error(err.response?.data?.message || 'Failed to initiate Razorpay');
    setProcessing(false);
  }
};

  // ── Stripe ────────────────────────────────────────
  const payWithStripe = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    try {
      const { data } = await api.post('/payments/stripe/create-intent', { orderId });
      const result = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) {
        toast.error(result.error.message);
      } else if (result.paymentIntent.status === 'succeeded') {
        await api.put(`/orders/${orderId}/pay`, {
          id: result.paymentIntent.id,
          status: result.paymentIntent.status,
        });
        onSuccess(orderId);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  // ── COD ──────────────────────────────────────────
  const payWithCOD = async () => {
    setProcessing(true);
    try {
      await api.put(`/orders/${orderId}/pay`, { id: 'COD', status: 'cod_pending' });
      onSuccess(orderId);
    } catch (err) {
      toast.error('Failed to confirm order');
    } finally {
      setProcessing(false);
    }
  };

  const handlePay = () => {
    if (method === 'razorpay') payWithRazorpay();
    else if (method === 'stripe') payWithStripe();
    else payWithCOD();
  };

  const cardStyle = {
    style: {
      base: { fontSize: '15px', color: '#212121', '::placeholder': { color: '#9e9e9e' } },
    },
  };

  return (
    <div className="checkout-form">
      <h2 className="step-title">💳 Payment Method</h2>

      {/* Method selector */}
      <div className="payment-methods">
        {[
          { id: 'razorpay', icon: '🇮🇳', label: 'Razorpay', desc: 'UPI, Cards, Netbanking, Wallets' },
          { id: 'stripe', icon: '🌐', label: 'Stripe', desc: 'International Credit / Debit Card' },
          { id: 'cod', icon: '💵', label: 'Cash on Delivery', desc: 'Pay when you receive' },
        ].map(m => (
          <label key={m.id} className={`payment-option ${method === m.id ? 'selected' : ''}`}>
            <input type="radio" name="payment" value={m.id} checked={method === m.id} onChange={() => setMethod(m.id)} />
            <span className="pay-icon">{m.icon}</span>
            <div>
              <strong>{m.label}</strong>
              <small>{m.desc}</small>
            </div>
          </label>
        ))}
      </div>

      {/* Razorpay note */}
      {method === 'razorpay' && (
        <div className="pay-note">
          🔒 You'll be redirected to Razorpay's secure payment gateway. Requires Razorpay script loaded in public/index.html.
        </div>
      )}

      {/* Stripe card */}
      {method === 'stripe' && (
        <div className="stripe-card-wrap">
          <label className="form-label">Card Details</label>
          <div className="stripe-card-element">
            <CardElement options={cardStyle} />
          </div>
        </div>
      )}

      {/* COD note */}
      {method === 'cod' && (
        <div className="pay-note">
          💡 Cash on Delivery is available. Pay the delivery person when your order arrives.
        </div>
      )}

      <div className="checkout-btns">
        <button className="btn btn-ghost" onClick={onBack} disabled={processing}>← Back</button>
        <button
          className="btn btn-primary btn-lg"
          onClick={handlePay}
          disabled={processing || (method === 'stripe' && !stripe)}
          style={{ flex: 1 }}
        >
          {processing ? '⏳ Processing...' : `Pay Now`}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Checkout page
// ─────────────────────────────────────────────────────────────
export default function Checkout() {
  const { items, subtotal, shippingCost, taxAmount, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = shipping, 2 = payment
  const [shippingData, setShippingData] = useState(null);
  const [orderId, setOrderId] = useState(null);

  const handleShippingNext = async (shippingAddress) => {
    setShippingData(shippingAddress);
    try {
      const orderItems = items.map(i => ({ product: i._id, quantity: i.quantity }));
      const { data } = await api.post('/orders', {
        orderItems,
        shippingAddress,
        paymentMethod: 'razorpay', // will be updated at payment step
      });
      setOrderId(data.order._id);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create order');
    }
  };

  const handlePaymentSuccess = (oid) => {
    clearCart();
    toast.success('🌿 Order placed successfully!');
    navigate(`/orders/${oid}`);
  };

  if (items.length === 0 && !orderId) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="checkout-page container">
      {/* Progress steps */}
      <div className="checkout-steps">
        <div className={`checkout-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'done' : ''}`}>
          <div className="step-circle">{step > 1 ? '✓' : '1'}</div>
          <span>Shipping</span>
        </div>
        <div className="step-connector"></div>
        <div className={`checkout-step ${step >= 2 ? 'active' : ''}`}>
          <div className="step-circle">2</div>
          <span>Payment</span>
        </div>
      </div>

      <div className="checkout-layout">
        {/* Left: steps */}
        <div className="checkout-left">
          {step === 1 && (
            <ShippingStep onNext={handleShippingNext} initialData={shippingData} />
          )}
          {step === 2 && orderId && (
            <Elements stripe={stripePromise}>
              <PaymentStep
                orderId={orderId}
                onSuccess={handlePaymentSuccess}
                onBack={() => setStep(1)}
              />
            </Elements>
          )}
        </div>

        {/* Right: order summary */}
        <div className="checkout-summary card">
          <h3 className="summary-title">Order Summary</h3>
          <div className="summary-items">
            {items.map(item => {
              const price = item.discountPrice || item.price;
              return (
                <div key={item._id} className="summary-item">
                  <img src={item.images?.[0]?.url} alt={item.name} className="summary-item-img" />
                  <div className="summary-item-info">
                    <p className="summary-item-name">{item.name}</p>
                    <p className="summary-item-qty">Qty: {item.quantity}</p>
                  </div>
                  <span className="summary-item-price">{formatPrice(price * item.quantity)}</span>
                </div>
              );
            })}
          </div>
          <hr className="divider" />
          <div className="summary-row"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
          <div className="summary-row"><span>Shipping</span><span>{shippingCost === 0 ? 'Free' : formatPrice(shippingCost)}</span></div>
          <div className="summary-row"><span>GST (18%)</span><span>{formatPrice(taxAmount)}</span></div>
          <hr className="divider" />
          <div className="summary-row summary-total"><strong>Total</strong><strong>{formatPrice(total)}</strong></div>
        </div>
      </div>
    </div>
  );
}
